/**
 * Global marketplace pulse panel — anonymized signal across ALL sellers.
 * Surfaces trending queries (WoW gain), top categories, and overall CTR.
 *
 * Source: GET /api/market/insights (public, no auth, no seller IDs leaked).
 */
import { useEffect, useState } from "react";
import { Activity, TrendingUp, Layers, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "@/lib/api";

export default function MarketInsightsPanel({ defaultDays = 14, onCategoryClick, onQueryClick }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [days, setDays] = useState(defaultDays);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setBusy(true);
    api
      .get(`/market/insights?days=${days}&limit=8`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setBusy(false));
  }, [open, days]);

  return (
    <div data-testid="market-insights-panel" className="glass-heavy rounded-2xl p-4">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="market-insights-toggle"
        className="w-full flex items-center gap-2 group"
      >
        <Activity size={14} className="text-[#8134AF]" />
        <div className="font-display font-bold text-sm">Market pulse</div>
        <div className="text-xs text-[#94A3B8] hidden sm:block">What the whole marketplace is searching for right now · anonymized</div>
        <div className="ml-auto flex items-center gap-1 text-xs text-[#8134AF]">
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </div>
      </button>

      {open && (
        <div className="mt-3 space-y-4" data-testid="market-insights-body">
          {/* Window picker */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">Window</span>
            {[7, 14, 30].map((d) => (
              <button
                key={d}
                data-testid={`market-window-${d}`}
                onClick={() => setDays(d)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition ${
                  days === d
                    ? "bg-[#8134AF] text-white border-[#8134AF]"
                    : "bg-white text-[#64748B] border-slate-200 hover:border-[#8134AF]"
                }`}
              >
                {d}d
              </button>
            ))}
            <div className="ml-auto text-[10px] text-[#94A3B8]">
              {data?.totals && `${data.totals.searches.toLocaleString()} searches · ${data.totals.ctr_pct}% CTR`}
            </div>
          </div>

          {busy && !data ? (
            <div className="text-center text-xs text-[#94A3B8] py-4">Loading pulse…</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Trending queries */}
              <div data-testid="trending-queries-card" className="bg-white/70 rounded-xl p-3 border border-slate-100">
                <div className="flex items-center gap-1.5 mb-2">
                  <TrendingUp size={12} className="text-[#DD2A7B]" />
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#DD2A7B] font-bold">Trending</div>
                  <div className="text-[10px] text-[#94A3B8] ml-1">vs prev window</div>
                </div>
                {(data?.trending_queries || []).length === 0 ? (
                  <div className="text-xs text-[#94A3B8] italic">No trending queries yet. Check back when more buyers search.</div>
                ) : (
                  <ul className="space-y-1.5">
                    {(data?.trending_queries || []).slice(0, 6).map((t, i) => {
                      const up = t.delta_pct > 0;
                      return (
                        <li key={`tr-${i}`} className="flex items-center gap-2">
                          <button
                            data-testid={`trending-query-${i}`}
                            onClick={() => onQueryClick?.(t.q)}
                            className="text-xs font-semibold text-[#0F172A] hover:text-[#DD2A7B] truncate flex-1 text-left"
                            title="Click to search"
                          >
                            {t.q}
                          </button>
                          <span
                            className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full tabular-nums shrink-0 ${
                              up ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {up ? "+" : ""}
                            {t.delta_pct}%
                          </span>
                          <span className="text-[10px] text-[#94A3B8] tabular-nums shrink-0">{t.this_week}×</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* Top categories */}
              <div data-testid="top-categories-card" className="bg-white/70 rounded-xl p-3 border border-slate-100">
                <div className="flex items-center gap-1.5 mb-2">
                  <Layers size={12} className="text-[#0EA5E9]" />
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#0EA5E9] font-bold">Top categories</div>
                </div>
                {(data?.top_categories || []).length === 0 ? (
                  <div className="text-xs text-[#94A3B8] italic">No category signal yet.</div>
                ) : (
                  <ul className="space-y-1.5">
                    {(data?.top_categories || []).slice(0, 6).map((c, i) => (
                      <li key={`cat-${i}`} className="flex items-center gap-2">
                        <button
                          data-testid={`top-category-${i}`}
                          onClick={() => onCategoryClick?.(c.category)}
                          className="text-xs font-semibold text-[#0F172A] hover:text-[#0EA5E9] truncate flex-1 text-left capitalize"
                          title="Click to filter"
                        >
                          {c.category}
                        </button>
                        <span className="text-[10px] font-bold text-[#0EA5E9] tabular-nums shrink-0">
                          {c.ctr_pct}%
                        </span>
                        <span className="text-[10px] text-[#94A3B8] tabular-nums shrink-0">{c.searches}×</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
